import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, Search, Clock } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useMushrooms } from "../context/MushroomsContext";
import { getCategoryLabel } from "../data/mushrooms";
import { useLang } from "../context/LanguageContext";

const CATEGORIES = ["all", "everyday", "premium", "exotic", "medicinal"];

function BadgeChip({ badge }) {
  if (!badge) return null;
  const key = badge.toLowerCase().replace(/\s+/g, '-').replace("'", "");
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full badge-${key}`}>{badge}</span>;
}

export default function ShopPage() {
  const { addToCart } = useCart();
  const { mushrooms, loading } = useMushrooms();
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const { t, lang } = useLang();

  const filtered = mushrooms.filter(m => {
    const matchCat = activeCategory === "all" || m.category === activeCategory;
    const matchSearch = !searchQuery || m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 sm:px-6" style={{ backgroundColor: 'var(--c-bg)' }}>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-8 sm:py-10 text-center" data-testid="shop-header">
          <p className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: '#D97A46' }}>{t('shop.label')}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" style={{ color: 'var(--c-text)' }}>{t('shop.title')}</h1>
          <p className="text-sm sm:text-base mt-2 max-w-lg mx-auto" style={{ color: 'var(--c-muted)' }}>{t('shop.subtitle', { n: mushrooms.length })}</p>
        </motion.div>

        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row gap-3 sm:gap-4 items-center">
          <div className="relative w-full sm:flex-1 sm:max-w-sm">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--c-muted)' }} />
            <input data-testid="shop-search-input" type="text" placeholder={t('shop.searchPlaceholder')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-full text-sm focus:outline-none focus:ring-2 transition-all"
              style={{ backgroundColor: 'var(--c-surface)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }} />
          </div>
          <div className="flex gap-2 flex-wrap justify-center" data-testid="category-filters">
            {CATEGORIES.map(cat => (
              <button key={cat} data-testid={`filter-${cat}`} onClick={() => setActiveCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all`}
                style={activeCategory === cat
                  ? { backgroundColor: 'var(--c-text)', color: 'var(--c-surface)' }
                  : { backgroundColor: 'var(--c-surface)', border: '1px solid var(--c-border)', color: 'var(--c-muted)' }
                }>
                {cat === "all" ? t('cat.all') : t(`cat.${cat}`)}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-72 rounded-2xl border animate-pulse" style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)' }} />)}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-20">
            <p className="text-lg" style={{ color: 'var(--c-muted)' }}>{t('shop.noResults')}</p>
            <button onClick={() => { setActiveCategory("all"); setSearchQuery(""); }} className="mt-4 text-sm hover:underline" style={{ color: '#D97A46' }}>{t('shop.clearFilters')}</button>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
            {filtered.map((mushroom, i) => (
              <motion.div key={mushroom.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="group rounded-2xl overflow-hidden flex flex-col hover:-translate-y-1 transition-transform duration-300"
                style={{ backgroundColor: 'var(--c-surface)', boxShadow: 'var(--c-shadow-md)', border: '1px solid var(--c-border)' }}
                data-testid={`mushroom-card-${mushroom.id}`}>
                <Link to={`/mushroom/${mushroom.id}`} className="relative h-36 sm:h-48 overflow-hidden">
                  <img src={mushroom.image} alt={mushroom.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-2 left-2 flex gap-1">
                    {mushroom.badge && <BadgeChip badge={mushroom.badge} />}
                    {mushroom.isUpcoming && (
                      <span className="text-[9px] sm:text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 flex items-center gap-0.5">
                        <Clock className="w-2.5 h-2.5" /> {t('shop.upcoming')}
                      </span>
                    )}
                  </div>
                  <div className="absolute top-2 right-2">
                    <span className="text-[9px] sm:text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-white/90 border border-white/50" style={{ color: 'var(--c-muted)' }}>{getCategoryLabel(mushroom.category)}</span>
                  </div>
                </Link>
                <div className="p-3 sm:p-4 flex flex-col gap-2 flex-1">
                  <div>
                    <Link to={`/mushroom/${mushroom.id}`}>
                      <h3 className="font-serif text-base sm:text-lg font-semibold hover:text-[#D97A46] transition-colors leading-tight" style={{ color: 'var(--c-text)' }}>{mushroom.name}</h3>
                    </Link>
                    <p className="text-[10px] sm:text-xs italic mt-0.5 hidden sm:block" style={{ color: 'var(--c-muted)' }}>{mushroom.scientificName}</p>
                  </div>
                  <p className="text-xs sm:text-sm leading-relaxed flex-1 line-clamp-2 hidden sm:block" style={{ color: 'var(--c-muted)' }}>
                    {(lang === 'hi' && mushroom.description_hi) ? mushroom.description_hi : mushroom.description}
                  </p>
                  <div className="flex items-center justify-between pt-1">
                    <div>
                      <span className="text-sm sm:text-base font-semibold" style={{ color: '#D97A46' }}>Rs.{mushroom.price}</span>
                      <span className="text-[10px] sm:text-xs ml-1" style={{ color: 'var(--c-muted)' }}>/ {mushroom.uom || "250g"}</span>
                      {mushroom.qty <= 10 && !mushroom.isUpcoming && <p className="text-[9px] sm:text-[10px] text-red-500 mt-0.5">{t('shop.onlyLeft', { n: mushroom.qty })}</p>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Link to={`/mushroom/${mushroom.id}`} data-testid={`view-details-${mushroom.id}`}
                        className="hidden sm:block px-3 py-1.5 rounded-full text-xs font-medium border transition-all hover:border-[#D97A46]/40" style={{ color: 'var(--c-muted)', borderColor: 'var(--c-border)' }}>
                        {t('shop.details')}
                      </Link>
                      {!mushroom.isUpcoming ? (
                        <button data-testid={`shop-add-to-cart-${mushroom.id}`} onClick={() => addToCart(mushroom)}
                          className="p-1.5 rounded-full text-white transition-all hover:shadow-md hover:-translate-y-0.5" style={{ backgroundColor: '#D97A46' }}>
                          <ShoppingCart className="w-4 h-4" />
                        </button>
                      ) : (
                        <Link to={`/mushroom/${mushroom.id}`}>
                          <span className="px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium text-amber-700 border border-amber-200 bg-amber-50">{t('shop.preOrder')}</span>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
